/**
 * FAQ content — static, curated copy (no network). One category per section
 * heading, one entry per Q&A.
 *
 * Adding a video to an answer: set `videoUrl` on the entry (full https URL).
 * The row then renders a "Watch video" link that opens in a new tab. Entries
 * without a videoUrl render text-only — no other change needed.
 */

export interface FaqEntry {
  question: string;
  answer: string;
  /** Optional walkthrough video for this answer — opens in a new tab. */
  videoUrl?: string;
}

export interface FaqCategory {
  /** Anchor id for the jump nav (stable, kebab-case). */
  id: string;
  title: string;
  entries: FaqEntry[];
}

export const FAQ_CATEGORIES: FaqCategory[] = [
  {
    id: 'general',
    title: 'General / Platform Overview',
    entries: [
      {
        question: 'What is REGARA and what does it do?',
        answer:
          'REGARA is an AI-assisted clinical and regulatory platform that helps medical device companies manage regulatory compliance, run AI-powered regulatory analyses, generate clinical documentation, and organize submission materials from one unified dashboard.',
      },
      {
        question: 'Who is the company behind REGARA?',
        answer:
          'The platform’s legal entity, per its Terms of Use, is Fai Tech, Inc., doing business as Regara™.',
      },
      {
        question:
          'What does the "Active Products," "Runs in Flight," "Needs Attention," and "Target Markets" summary on the dashboard mean?',
        answer:
          'These are portfolio-level metrics showing how many products you have registered, how many AI agent analyses are currently running, how many items require follow-up (e.g., failed runs), and how many of your target regulatory markets have been addressed.',
      },
      {
        question: 'How is my "Portfolio Mix" and "Market Coverage" calculated?',
        answer:
          'Portfolio Mix breaks down your registered products by device/drug/biologic type, while Market Coverage shows how many products have completed agent work for each target regulatory market (e.g., European Union, United States).',
      },
    ],
  },
  {
    id: 'company-product',
    title: 'Company & Product Accounts',
    entries: [
      {
        question: 'How do I register a new company on REGARA?',
        answer:
          'From the Company Account page you can click "Create New Company" to add a new company entry, which tracks its legal structure, submission status, and target markets.',
      },
      {
        question: 'How do I add a new product for regulatory analysis?',
        answer:
          'From the Product Account page, click "Create New Product" to register a new device, drug, or biologic, including details like classification, intended use, sterility, and reusability.',
      },
      {
        question: 'Can I download a technical specification for a registered product?',
        answer:
          'Yes, each product row includes a "Download tech-spec PDF" option along with edit and delete controls.',
      },
    ],
  },
  {
    id: 'predicate-finder',
    title: 'Existing Products',
    entries: [
      {
        question: 'What does the Existing Products agent do?',
        answer:
          'It finds and scores the existing product your submission is compared against — a predicate or comparator device, a reference drug, or a reference biologic — with confidence levels and the gaps to address.',
      },
      {
        question: 'How does REGARA determine which predicate device is "primary"?',
        answer:
          'The system suggests a top-scored match by default (verified against openFDA data), but selecting the primary predicate for your dossier is presented as the user’s own regulatory decision.',
      },
      {
        question: 'What are "gaps to address" in a predicate match?',
        answer:
          'These are specific differences between your device and the candidate predicate (e.g., material, geometry, or testing differences) that may need additional justification or bench testing before submission.',
      },
      {
        question: 'Can I trust the Existing Products agent results as final regulatory guidance?',
        answer:
          'No — the tool includes an explicit disclaimer that its output is AI-generated decision support, not legal or regulatory advice, and recommends review by a qualified regulatory professional.',
      },
    ],
  },
  {
    id: 'regulatory-strategy',
    title: 'Regulatory Strategy',
    entries: [
      {
        question: 'What does the Regulatory Strategy agent produce?',
        answer:
          'A full regulatory roadmap including recommended pathway (e.g., CE-MDR), proposed device classification, clinical data requirements, a project timeline, and a risk register grounded in regulatory data.',
      },
      {
        question: 'How does REGARA determine a device’s risk classification?',
        answer:
          'It applies the relevant regulatory framework’s classification rules (for example, MDR Annex VIII Rule 8 for implantable devices) and explains the rationale, including which risk-escalating criteria do or do not apply.',
      },
      {
        question: 'What kind of risks does the "Risk Register" flag?',
        answer:
          'Typical risks include Notified Body capacity constraints, clinical evidence sufficiency, biocompatibility data gaps, and post-market vigilance obligations, each paired with a suggested mitigation.',
      },
      {
        question: 'Does the tool tell me which Notified Body or regulatory authority to use?',
        answer:
          'Yes, it can recommend candidate Notified Bodies based on scope and geography, and identifies relevant competent authorities for each target market.',
      },
    ],
  },
  {
    id: 'clinical',
    title: 'Clinical Protocols & Clinical Study Reports',
    entries: [
      {
        question: 'What does the Clinical Protocols agent help create?',
        answer:
          'A regulator-aligned clinical trial protocol, including inclusion/exclusion criteria, endpoints, and a statistical plan, informed by trial-registry precedents and current regulatory guidance.',
      },
      {
        question: 'What is the Clinical Study Report (CSR) agent for?',
        answer:
          'It generates a regulator-aligned Clinical Study Report following ICH E3 structure, covering efficacy and safety findings and statistical analysis based on your product’s clinical evidence.',
      },
    ],
  },
  {
    id: 'submission-dossier',
    title: 'Submission Dossier',
    entries: [
      {
        // PENDING RA REVIEW — covers drugs and biologics (was device / 510(k) only).
        question: 'What does the Submission Dossier agent assemble?',
        answer:
          'A complete regulatory submission package built for your product’s pathway. For a medical device: predicate or comparator comparison, technological characteristics, performance data, labeling, and the equivalence analysis. For a drug or biologic: quality (CMC), nonclinical and clinical evidence, and labeling — plus, for a generic or biosimilar, the reference drug or reference product and the bioequivalence or biosimilarity data.',
      },
    ],
  },
  {
    id: 'vault',
    title: 'Vault & Collaboration',
    entries: [
      {
        question: 'What is stored in the Vault & Collaboration section?',
        answer:
          'Every regulatory document you’ve generated or uploaded, organized by domain (device/drug/biologic), document type (predicates, strategy, protocols, reports, dossiers), and status (draft, in review, approved, etc.).',
      },
      {
        question: 'Can multiple team members collaborate on documents in the Vault?',
        answer:
          'The Vault is designed for secure, versioned document storage intended for team review and collaboration, with status tracking such as "Working Copy," "Baselined," and "Approved."',
      },
    ],
  },
  {
    id: 'terms-legal',
    title: 'Terms, Legal & Trust',
    entries: [
      {
        question: 'What legal documents govern my use of REGARA?',
        answer:
          'The Terms & Conditions page lists the Terms of Use, Privacy Policy, Data Processing Agreement, AI Output Disclaimer, Data Use & AI Non-Training Agreement, and an amendment to the Master Subscription Agreement.',
      },
      {
        question: 'Does REGARA use my data to train its AI models?',
        answer:
          'There’s a dedicated "Data Use & AI Non-Training Agreement" in the legal documents that addresses this directly — review that document’s specific terms for details.',
      },
      {
        question: 'Is REGARA’s AI output considered official regulatory advice?',
        answer:
          'No. Both the Existing Products agent and Regulatory Strategy results carry explicit disclaimers stating the output is AI-generated decision support, not legal or regulatory advice, and final regulatory decisions rest with the relevant authority.',
      },
    ],
  },
  {
    id: 'device-classification',
    title: 'Device Classification & Pathway Determination',
    entries: [
      {
        question:
          'How does REGARA determine my device’s regulatory classification, and does it show its reasoning?',
        answer:
          'The Regulatory Strategy agent proposes a classification with a documented rationale. In the sample device it classified a titanium suture anchor as EU MDR Class IIb under Annex VIII, Chapter III, Rule 8 (implantable device), and explicitly walked through each Class III escalator (heart/CNS/central circulatory contact, absorbability, medicinal action, active implant, joint/spinal replacement, etc.) to show why none applied. That level of rule-by-rule justification is what you’d want to lift into a classification memo — after verification.',
      },
      {
        question: 'Does it recommend a specific conformity assessment route?',
        answer:
          'Yes. For the Class IIb implantable example it recommended MDR Annex IX (QMS plus technical documentation assessment) and, importantly, flagged the Article 52(4) second-subparagraph nuance that suture anchors are not within the exemption list (sutures, staples, screws, pins, clips, connectors), so the Notified Body must assess technical documentation for every device rather than a representative sample.',
      },
      {
        question: 'Will it tell me whether an expert panel (CECP) consultation is triggered?',
        answer:
          'It does. In the example it concluded CECP was not required because that procedure applies to Class III implantables and certain Class IIb active drug-delivery devices, neither of which applied — while still pointing to relevant published CECP opinions as benchmarking for clinical evidence expectations.',
      },
    ],
  },
  {
    id: 'predicate-identification',
    title: 'Predicate Identification (US 510(k))',
    entries: [
      {
        question: 'Where do the predicate matches come from, and are the identities reliable?',
        answer:
          'The Existing Products agent scores candidate predicates and verifies device identity (K-number, applicant, clearance date) against FDA’s openFDA database, labeling each as "Verified · openFDA." Scores and the substantial-equivalence analysis, however, are explicitly framed as recommendations to confirm with a qualified regulatory professional.',
      },
      {
        question: 'Does it help with the substantial-equivalence argument itself?',
        answer:
          'Yes — for each candidate it lists specific SE points (identical product code and regulation number, same intended use, same indications, same technological characteristics/materials) alongside a distinct "gaps to address" section (e.g., anchor geometry differences, material differences like PEEK vs. titanium, inserter design). It also assigns a primary predicate by default while stating that choosing the primary predicate is the user’s regulatory decision.',
      },
      {
        question: 'Does it surface post-market signal data on predicates?',
        answer:
          'It shows a MAUDE adverse-event count (by FDA product code, all years) per predicate and constructs a predicate chain (e.g., a device tracing back through earlier clearances to a 1995 foundational predicate), which is useful for demonstrating lineage depth.',
      },
      {
        question: 'Will it tell me when my own submission data is insufficient?',
        answer:
          'Yes. The result includes a "Limitations of this Analysis" section listing characteristics it couldn’t find in your product data or uploaded specification — e.g., quantitative performance values (pull-out strength, cyclic loading), a formal SE comparison table, ISO 10993 biocompatibility reports, or ISO 11137 sterility validation — and prompts you to add them and re-run for a stronger match.',
      },
    ],
  },
  {
    id: 'evidence-standards',
    title: 'Evidence & Standards Requirements',
    entries: [
      {
        question: 'Does REGARA specify what clinical evidence is required?',
        answer:
          'The Regulatory Strategy output details clinical evaluation expectations, including (in the EU example) the CER under MDR Annex XIV Part A, the Article 61(4) presumption that implantables require clinical investigations unless same-manufacturer equivalence is shown, the equivalence route per MDCG 2020-5 across technical/biological/clinical dimensions, and PMCF obligations with annual updates.',
      },
      {
        question: 'Does it map applicable standards and test methods?',
        answer:
          'Yes, it lists them concretely — for the example device: ISO 13485, ISO 14971, the ISO 10993 series (with ISO 10993-6 implantation flagged as critical for a permanent implant), ISO 5832-3 / ASTM F136 for the titanium alloy, ASTM F543 for pull-out testing, ISO 11137 for radiation sterilization, and ISO 11607 for sterile-barrier packaging.',
      },
      {
        question: 'Does it address market-specific labeling and registration?',
        answer:
          'For the EU example it called out EUDAMED registration, UDI/Basic UDI-DI assignment via a GS1 issuing agency, the implant card per Article 18 / MDCG 2019-8, the SSCP per Article 32 / MDCG 2019-9, the correct EMDN code, and language requirements for the specific target markets (e.g., Greek and German IFUs), plus the relevant competent authorities.',
      },
    ],
  },
  {
    id: 'strategy-timeline-risk',
    title: 'Strategy, Timeline & Risk',
    entries: [
      {
        question: 'Does it give a realistic project timeline?',
        answer:
          'Yes — a phased timeline with dependencies, from gap analysis/QMS augmentation and technical documentation preparation through Notified Body pre-submission, Stage 1 and Stage 2 audits, technical documentation assessment, certificate issuance, EUDAMED registration, and market placement, with an overall target range (18–24 months in the example).',
      },
      {
        question: 'Is there a structured risk register I can use?',
        answer:
          'There is a risk register with impact ratings and mitigations — covering items like Notified Body capacity/queue times, clinical evidence sufficiency, the per-device documentation assessment burden, biocompatibility data gaps, EUDAMED availability, and post-market vigilance timelines (e.g., 15-day serious incident reporting).',
      },
    ],
  },
  {
    id: 'documentation-deliverables',
    title: 'Documentation Deliverables',
    entries: [
      {
        question: 'What documents can the agents actually draft?',
        answer:
          'The AI Output Disclaimer defines "Regulatory Output" to include drafted submission sections, predicate comparisons, claims, regulatory strategy, and clinical protocol and report drafts. The Clinical Protocols agent generates a regulator-aligned protocol (inclusion/exclusion, endpoints, statistical plan, safety monitoring); the Clinical Study Report agent produces an ICH E3–structured CSR; and the Submission Dossier agent assembles a full submission package for a device, drug or biologic, built for its pathway (see Submission Dossier above).',
      },
      {
        question: 'How does version control and review status work?',
        answer:
          'The Vault stores generated and uploaded documents with lifecycle statuses — Draft, Working Copy, Baselined, In review, Approved, and Superseded — filterable by domain (device/drug/biologic) and type (predicates, strategy, protocols, reports, dossiers), which supports a controlled review workflow.',
      },
    ],
  },
  {
    id: 'compliance-data-governance',
    title: 'Compliance, Liability & Data Governance (critical for RA sign-off)',
    entries: [
      {
        question: 'Can I file REGARA’s output directly with a regulator?',
        answer:
          'No. The AI Output Disclaimer is explicit that Regulatory Output is decision-support only, "not a fileable regulatory submission," may contain errors, hallucinated citations, or outdated guidance, and must be reviewed and validated by qualified regulatory professionals before use. You separately acknowledge this at registration.',
      },
      {
        question: 'Who bears responsibility for the accuracy of a submission built with REGARA?',
        answer:
          'The Terms of Use place sole responsibility on the customer for all submissions, for the accuracy/completeness/compliance of any output used, and for decisions made in reliance on it. Regara disclaims warranties that output is accurate, complete, or suitable for submission, and states no professional or advisory relationship is created.',
      },
      {
        question: 'Is my confidential regulatory data used to train the AI models?',
        answer:
          'The Privacy Policy and Data Use & AI Non-Training Agreement state that customer data, inputs, and outputs are not used to train, fine-tune, or improve any AI model, and that the third-party model provider operates under no-training, strictly transient-retention terms. Any consent to training is governed exclusively by a separately executed Data Use & AI Training Agreement (per Amendment No. 1 to the MSA).',
      },
      {
        question: 'How is PHI and personal data handled?',
        answer:
          'PHI requires an executed Business Associate Agreement before submission, personal data is governed by the Data Processing Agreement, and both PHI and special-category data are unconditionally excluded from any training use. Security controls cited include AES-256 at rest, TLS 1.2+ in transit, RBAC, and MFA.',
      },
      {
        question: 'Can I get audit/compliance evidence for my own quality system?',
        answer:
          'The Non-Training Agreement provides for annual compliance verification, satisfiable via a SOC 2 Type II report or comparable third-party audit, and lets you request written confirmation that the no-training commitments with the model provider remain in effect — useful supporting evidence for supplier qualification under ISO 13485.',
      },
    ],
  },
];
